import {
  IUseCase,
  GetSolicitationMetricsUseCaseRequest,
  GetSolicitationMetricsUseCaseResponse,
} from './interfaces'
import { ISolicitationRepository } from '@/core/domain/repositories/ISolicitationRepository'
import { Result, createOk, createErr, isOk } from '@/shared/utils/Result'
import { ValidationError } from '@/shared/errors'
import { DateRangeString } from '@/shared/types'

export class GetSolicitationMetricsUseCase
  implements
    IUseCase<
      GetSolicitationMetricsUseCaseRequest,
      GetSolicitationMetricsUseCaseResponse
    >
{
  constructor(
    private readonly solicitationRepository: ISolicitationRepository
  ) {}

  async execute(
    request: GetSolicitationMetricsUseCaseRequest
  ): Promise<Result<GetSolicitationMetricsUseCaseResponse, Error>> {
    try {
      // Validate input
      if (!request.userId || request.userId.trim() === '') {
        return createErr(new ValidationError('User ID is required'))
      }

      // Build date range if provided
      const dateRange: DateRangeString | undefined =
        request.dateStart && request.dateEnd
          ? { start: request.dateStart, end: request.dateEnd }
          : undefined

      // Get solicitation metrics from repository
      const solicitationAggregatesResult =
        await this.solicitationRepository.getSolicitationAggregates(
          request.userId,
          dateRange
        )

      // Check for errors
      if (!isOk(solicitationAggregatesResult)) {
        return createErr(solicitationAggregatesResult.error)
      }

      return createOk({
        totalSolicitations:
          solicitationAggregatesResult.value.totalSolicitations ?? 0,
        acceptedSolicitations:
          solicitationAggregatesResult.value.acceptedSolicitations ?? 0,
        refusedSolicitations:
          solicitationAggregatesResult.value.refusedSolicitations ?? 0,
        canceledSolicitations:
          solicitationAggregatesResult.value.canceledSolicitations ?? 0,
        lastUpdatedAt: solicitationAggregatesResult.value.lastUpdatedAt,
      })
    } catch (error) {
      console.error('GetSolicitationMetricsUseCase failed:', error)
      return createErr(
        error instanceof Error
          ? error
          : new Error('Failed to get solicitation metrics')
      )
    }
  }
}
